import { lazy } from 'react'
import type { ComponentType, LazyExoticComponent } from 'react'

/**
 * Wrapper around React.lazy that handles both named and default exports.
 *
 * - For a default export, omit `exportName`: `lazyPage(() => import('./Foo'))`
 * - For a named export, pass the export name: `lazyPage(() => import('./Foo'), 'Foo')`
 */

// Overload 1: named export
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyPage<K extends string, C extends ComponentType<any>>(
  factory: () => Promise<Record<K, C>>,
  exportName: K,
): LazyExoticComponent<C>

// Overload 2: default export
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyPage<C extends ComponentType<any>>(
  factory: () => Promise<{ default: C }>,
): LazyExoticComponent<C>

// Implementation signature — not visible to callers.
// `any` is required here because the two overloads have divergent shapes
// (`{ default }` vs `Record<K, C>`) that can't be satisfied by a single
// concrete type. The public overloads fully constrain caller types.
/* eslint-disable @typescript-eslint/no-explicit-any */
export function lazyPage(
  factory: () => Promise<any>,
  exportName?: string,
): LazyExoticComponent<ComponentType<any>> {
  /* eslint-enable @typescript-eslint/no-explicit-any */
  return lazy(async () => {
    const mod = await factory()
    return {
      default: exportName ? mod[exportName] : mod.default,
    }
  })
}
